import { restNdjson } from "./ndjsonClient";
import type { ChatStreamEvent, ChatStreamRequest, DoneEvent } from "../types";

export type ChatMessageObject = {
  id: string;
  role: string;
  content: string;
  createdAt: string;
  metadata?: Record<string, unknown> | null;
  summary?: string | null;
};

export type ChatCompletion = {
  conversationId: string;
  requestMessageObject?: ChatMessageObject | null;
  responseMessageObject?: ChatMessageObject | null;
  url?: string | null;
};

export type ChatProgressEvent = {
  type: "progress";
  content: string;
};

export type ChatCompletionStreamEvent = {
  type: "completion";
  completion: ChatCompletion;
};

export type ChatStreamYield = ChatProgressEvent | ChatCompletionStreamEvent;

function readEmbedUrl(metadata: Record<string, unknown> | null | undefined): string | null {
  if (!metadata) return null;
  const value = metadata.embed_url;
  return typeof value === "string" ? value : null;
}

function isoFromEpochMs(timestamp: number): string {
  return new Date(timestamp).toISOString();
}

function mapDoneEvent(event: DoneEvent): ChatCompletion {
  const rawReq = event.request_message_object;
  const rawRes = event.response_message_object;
  const rawSummarizeRes = event.message;

  return {
    conversationId: event.conversation_id,
    requestMessageObject: rawReq
      ? {
          id: rawReq.id,
          role: rawReq.role,
          content: rawReq.content,
          createdAt: rawReq.created_at,
          metadata: rawReq.metadata,
        }
      : null,
    responseMessageObject: rawRes
      ? {
          id: rawRes.id,
          role: rawRes.role,
          content: rawRes.content,
          createdAt: rawRes.created_at,
          metadata: rawRes.metadata,
        }
      : rawSummarizeRes
        ? {
            id: rawSummarizeRes.id,
            role: rawSummarizeRes.role,
            content: rawSummarizeRes.content,
            createdAt: isoFromEpochMs(event.timestamp),
            metadata: rawSummarizeRes.metadata,
            summary: event.summary,
          }
        : null,
    // Top-level done.url is raw LLM output and may be malformed — prefer metadata.embed_url.
    url: readEmbedUrl(rawRes?.metadata) ?? event.url ?? null,
  };
}

function toYield(event: ChatStreamEvent): ChatStreamYield | null {
  switch (event.event) {
    case "error":
      throw new Error(event.content);
    case "done":
      return { type: "completion", completion: mapDoneEvent(event) };
    case "tool_call":
      return { type: "progress", content: event.content };
    case "thought":
      return { type: "progress", content: event.content };
    // `start`, `conversation`, `embed_url`, and `token` are part of the wire format
    // but not surfaced to the hook today — early conversationId / iframe paint can
    // be wired in later by yielding additional discriminated shapes here.
    case "start":
    case "conversation":
    case "embed_url":
    case "token":
      return null;
    default:
      return null;
  }
}

const CHAT_STREAM_PATH = "/ai/insights/chat/stream";

export async function* sendChatMessage(
  request: ChatStreamRequest,
): AsyncGenerator<ChatStreamYield, void, void> {
  for await (const event of restNdjson<ChatStreamEvent>(CHAT_STREAM_PATH, {
    method: "POST",
    body: request,
  })) {
    const yielded = toYield(event);
    if (!yielded) continue;

    yield yielded;
    if (yielded.type === "completion") return;
  }

  throw new Error("Chat stream ended without a completion event");
}
